/**
 * Deterministic local demo state for the sandboxed renter walkthrough.
 *
 * The one real, intentionally-seeded `is_demo` company (fetched once,
 * read-only: see useDemoCompany() below) is the single source of truth for
 * names/prices/contact info; everything from here on (the fabricated
 * booking reference, confirmation token, dates) is local, in-memory demo
 * state that is never sent to the real backend. No part of the walkthrough
 * writes to the real database.
 */
import { useEffect, useState } from "react";
import { INCIDENT_SCENARIO } from "./incidentScenario";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

// The homepage walkthrough is a sandbox, so it must never depend on the API
// merely to become usable. This deterministic local company is rendered
// immediately and may be replaced by the seeded live demo company when that
// read-only request succeeds.
export const LOCAL_DEMO_COMPANY = Object.freeze({
  id: "demo-dumaguete-island-wheels",
  public_slug: "dumaguete-island-wheels-demo",
  name: "Dumaguete Island Wheels",
  city: "Dumaguete City",
  is_demo: true,
  online_booking_enabled: true,
  public_description_full: "A fictional Dumaguete rental business used only to demonstrate the ConfirmPH renter journey.",
  public_pickup_locations: ["Dumaguete City office"],
  contact: { email: "hello@dumaguete-wheels.example", phone: "+63 917 555 0108" },
  rating_avg: 4.9,
  rating_count: 2,
  reviews: [
    { id: "demo-review-1", rating: 5, comment: "Clear booking details and an easy pickup.", customer_name: "Maria S." },
    { id: "demo-review-2", rating: 5, comment: "The confirmation page made everything easy to check.", customer_name: "Carlo D." },
  ],
  vehicles: [
    {
      id: "demo-vios-2211", name: "Toyota Vios", category: "Sedan",
      transmission: "Automatic", seats: 5, price_per_day: 2400,
      deposit_amount: 2000, status: "available",
    },
    {
      id: "demo-mirage-5102", name: "Mitsubishi Mirage", category: "Hatchback",
      transmission: "Automatic", seats: 5, price_per_day: 2100,
      deposit_amount: 1800, status: "available",
    },
  ],
});

export function useDemoCompany() {
  const [company, setCompany] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const listRes = await fetch(`${API}/public/rentals?sort=newest`);
        if (!listRes.ok) throw new Error("Demo listing unavailable");
        const listData = await listRes.json();
        const items = Array.isArray(listData) ? listData : listData?.items || [];
        const first = items.find((c) => c.is_demo) || items[0];
        if (!first) { if (!cancelled) setError(true); return; }
        const slug = first.public_slug || first.id;
        const profileRes = await fetch(`${API}/public/rentals/${slug}`);
        if (!profileRes.ok) throw new Error("Demo profile unavailable");
        const profileData = await profileRes.json();
        if (!cancelled) setCompany(profileData);
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return { company, error };
}

function pad(n) { return String(n).length < 2 ? `0${n}` : String(n); }

/** A fixed, plausible near-future weekend: deterministic within a single
 * page load (computed once, not re-derived per render) so dates don't drift
 * mid-walkthrough. */
function demoDates() {
  const now = new Date();
  const start = new Date(now.getTime() + 6 * 24 * 3600 * 1000);
  start.setHours(9, 0, 0, 0);
  const end = new Date(start.getTime() + 2 * 24 * 3600 * 1000);
  end.setHours(18, 0, 0, 0);
  return { startIso: start.toISOString(), endIso: end.toISOString() };
}

/**
 * Builds the full set of local demo objects the walkthrough steps need,
 * from the one real fetched company. Call once per mount (ProductWalkthrough
 * memoizes this): every screen reads from this same object, so switching
 * Desktop/Phone or stepping back and forward never re-derives or drifts.
 */
export function buildDemoState(company) {
  const vehicle = (company.vehicles || [])[0] || null;
  const { startIso, endIso } = demoDates();
  const bookingId = "demo-booking-1";
  const bookingRef = "CPH-DEMO1";
  const price = vehicle?.price_per_day ? vehicle.price_per_day * 2 : 4800;
  const deposit = vehicle?.deposit_amount ?? 2000;

  const baseBooking = {
    id: bookingId,
    booking_reference: bookingRef,
    start_datetime: startIso,
    end_datetime: endIso,
    price,
    deposit_required: deposit,
    customer_name: "Alex Renter",
    customer_email: "alex@example.com",
    customer_phone: "0917 000 1234",
    pickup_location: (company.public_pickup_locations || [])[0] || "Business office",
    return_location: (company.public_pickup_locations || [])[0] || "Business office",
    customer_visible_notes: null,
    rental_service_type: "self_drive",
    is_hold: false,
    change_requested: false,
    cancelled_by: null,
    cancellation_reason: null,
    reconfirmation_required: false,
    risk_status: "ok",
  };

  const demoVehicleForConfirm = vehicle ? {
    id: vehicle.id, name: vehicle.name, category: vehicle.category || "car",
    transmission: vehicle.transmission, seats: vehicle.seats, plate_number: "CPH-DEMO",
  } : { id: "demo-vehicle", name: "Demo vehicle", category: "car", transmission: "Automatic", seats: 5, plate_number: "CPH-DEMO" };

  const demoDamages = [
    { id: "d1", tag_code: "small_scratch", severity: "small", view: "front", x_pct: 38, y_pct: 52 },
    { id: "d2", tag_code: "small_dent", severity: "small", view: "left", x_pct: 62, y_pct: 40 },
  ];

  // "What if something goes wrong?" branch: a second vehicle to reassign
  // to. Falls back to a clearly-labeled fabricated one if the real seeded
  // demo company only has one vehicle, same discipline as demoVehicleForConfirm.
  const issueCompany = INCIDENT_SCENARIO.business;
  const issueVehicle = INCIDENT_SCENARIO.damagedVehicle;
  const replacementVehicle = INCIDENT_SCENARIO.replacementVehicle;
  const issueBooking = {
    ...baseBooking,
    id: INCIDENT_SCENARIO.booking.id,
    booking_reference: INCIDENT_SCENARIO.booking.reference,
    customer_name: INCIDENT_SCENARIO.affectedRenter.name,
    customer_email: INCIDENT_SCENARIO.affectedRenter.email,
    customer_phone: INCIDENT_SCENARIO.affectedRenter.phone,
  };

  const issueDetectedAt = new Date().toISOString();
  const demoWarning = {
    id: "demo-warning-1",
    warning_type: "vehicle_unavailable",
    severity: "critical",
    status: "active",
    title: "Booked vehicle unavailable",
    message: `${issueVehicle.name} · ${issueVehicle.plate_number} is unavailable and is assigned to booking ${INCIDENT_SCENARIO.booking.reference}. The booking may be affected.`,
    recommended_action: "Reassign a replacement vehicle or contact the renter.",
    related_booking_id: INCIDENT_SCENARIO.booking.id,
    related_vehicle_id: issueVehicle.id,
    related_incident_id: null,
    internal_notes: [],
    detected_at: issueDetectedAt,
    last_detected_at: issueDetectedAt,
    closed_at: null,
    closure_reason: null,
  };

  // ReassignVehicleModal `preloaded`: mirrors GET /bookings/{id}/replacement-candidates.
  const replacementPreloaded = {
    booking: {
      booking_reference: INCIDENT_SCENARIO.booking.reference,
      customer_name: INCIDENT_SCENARIO.affectedRenter.name,
      current_vehicle: { name: issueVehicle.name, plate_number: issueVehicle.plate_number },
      start_datetime: startIso,
      end_datetime: endIso,
    },
    candidates: [
      {
        id: replacementVehicle.id, name: replacementVehicle.name, plate_number: replacementVehicle.plate_number,
        availability: "available", availability_label: "Available",
        category_match: replacementVehicle.category === demoVehicleForConfirm.category,
        category: replacementVehicle.category, transmission: replacementVehicle.transmission,
        seats: replacementVehicle.seats, price_per_day: replacementVehicle.price_per_day,
        vehicle_warnings: [], conflicts: { has_critical: false },
      },
    ],
  };

  // CustomerConfirmation `preloaded` for the post-reassignment reconfirmation
  // step: a real version_diff shape (see CustomerConfirmation.jsx's
  // DISPLAY_FIELDS: flat snapshot fields, not nested).
  const reconfirmVersionDiff = {
    changed_fields: ["item_id"],
    previous_confirmed_snapshot: { vehicle_name: `${issueVehicle.name} (${issueVehicle.plate_number})` },
    current_snapshot: { vehicle_name: `${replacementVehicle.name} (${replacementVehicle.plate_number})` },
  };

  return {
    company,
    vehicle,
    bookingId,
    bookingRef,
    price,
    deposit,

    // Step: Confirmation page (CustomerConfirmation `preloaded`)
    confirmationPreloaded: {
      booking: { ...baseBooking, status: "awaiting_customer_confirmation" },
      vehicle: demoVehicleForConfirm,
      company: { name: company.name, city: company.city, email: company.contact?.email, phone: company.contact?.phone },
      expired: false,
      token_status: "active",
      terms: null,
      damages: demoDamages,
      version_diff: null,
    },

    // Step: Booking confirmed (CustomerBookingDetail `preloaded`, status confirmed)
    confirmedPreloaded: {
      booking: { ...baseBooking, status: "confirmed" },
      company: { id: company.id, name: company.name, city: company.city, email: company.contact?.email, phone: company.contact?.phone },
      vehicle: demoVehicleForConfirm,
      confirmation_versions: [
        { id: "v1", version_number: 1, trigger: "booking_created", created_by_type: "staff", created_at: startIso, sent_at: startIso, confirmed_at: startIso, confirmed_by_type: "customer" },
      ],
      deposit: { status: "pending", required: deposit, collected: 0 },
      review_status: { invited: false },
    },

    // Step: Ongoing
    ongoingPreloaded: {
      booking: { ...baseBooking, status: "ongoing" },
      company: { id: company.id, name: company.name, city: company.city, email: company.contact?.email, phone: company.contact?.phone },
      vehicle: demoVehicleForConfirm,
      confirmation_versions: [
        { id: "v1", version_number: 1, trigger: "booking_created", created_by_type: "staff", created_at: startIso, sent_at: startIso, confirmed_at: startIso, confirmed_by_type: "customer" },
      ],
      deposit: { status: "pending", required: deposit, collected: 0 },
      review_status: { invited: false },
    },

    // Step: Completed (Book again + review invite become relevant)
    completedPreloaded: {
      booking: { ...baseBooking, status: "completed" },
      company: { id: company.id, name: company.name, city: company.city, email: company.contact?.email, phone: company.contact?.phone },
      vehicle: demoVehicleForConfirm,
      confirmation_versions: [
        { id: "v1", version_number: 1, trigger: "booking_created", created_by_type: "staff", created_at: startIso, sent_at: startIso, confirmed_at: startIso, confirmed_by_type: "customer" },
      ],
      deposit: { status: "released", required: deposit, collected: deposit, refunded: deposit },
      review_status: { invited: true, submitted: false },
    },

    // Step: Review invite (ReviewInvite `preloaded`)
    reviewPreloaded: {
      business_name: company.name,
      customer_name: "Alex",
    },

    // --- "What if something goes wrong?" branch ---
    replacementVehicle,
    issueBookingId: INCIDENT_SCENARIO.booking.id,
    demoWarning,
    replacementPreloaded,

    // Renter view: the real My Booking page, still Confirmed, flagged with
    // the real risk_status a directly-affected booking actually gets
    // (see backend/incidents/detection_service.py's risk_for_impact -
    // "directly_affected" -> "affected_action_needed") so the real
    // RiskPill + STATUS_EXPLAINERS copy render, instead of an invented
    // notification bubble.
    atRiskPreloaded: {
      booking: { ...issueBooking, status: "confirmed", risk_status: "affected_action_needed", customer_visible_notes: INCIDENT_SCENARIO.safeNotice },
      company: issueCompany,
      vehicle: issueVehicle,
      confirmation_versions: [
        { id: "v1", version_number: 1, trigger: "booking_created", created_by_type: "staff", created_at: startIso, sent_at: startIso, confirmed_at: startIso, confirmed_by_type: "customer" },
      ],
      deposit: { status: "pending", required: deposit, collected: 0 },
      review_status: { invited: false },
    },

    issueCancelledPreloaded: {
      booking: { ...issueBooking, status: "cancelled", cancelled_by: "business", cancellation_reason: "Assigned vehicle unavailable; no suitable replacement was available.", risk_status: "ok", customer_visible_notes: "Traveler Car Rental could not provide a suitable replacement and cancelled this booking. Contact the business about any payment or refund arrangements." },
      company: issueCompany,
      vehicle: issueVehicle,
      confirmation_versions: [
        { id: "incident-v1", version_number: 1, trigger: "booking_created", created_by_type: "staff", created_at: startIso, sent_at: startIso, confirmed_at: startIso, confirmed_by_type: "customer" },
      ],
      deposit: { status: "pending", required: deposit, collected: 0 },
      review_status: { invited: false },
    },

    // Renter view: the confirmation link re-sent after reassignment, with a
    // real pending-change diff to review (CustomerConfirmation `preloaded`).
    reconfirmPreloaded: {
      booking: { ...issueBooking, status: "awaiting_customer_confirmation", reconfirmation_required: true },
      vehicle: replacementVehicle,
      company: issueCompany,
      expired: false,
      token_status: "active",
      terms: null,
      damages: demoDamages,
      version_diff: reconfirmVersionDiff,
    },

    // Renter view: booking reconfirmed with the new vehicle.
    reconfirmedPreloaded: {
      booking: { ...issueBooking, status: "confirmed", item_id: replacementVehicle.id },
      company: issueCompany,
      vehicle: replacementVehicle,
      confirmation_versions: [
        { id: "v1", version_number: 1, trigger: "booking_created", created_by_type: "staff", created_at: startIso, sent_at: startIso, confirmed_at: startIso, confirmed_by_type: "customer" },
        { id: "v2", version_number: 2, trigger: "vehicle_reassigned", created_by_type: "staff", created_at: issueDetectedAt, sent_at: issueDetectedAt, confirmed_at: new Date().toISOString(), confirmed_by_type: "customer" },
      ],
      deposit: { status: "pending", required: deposit, collected: 0 },
      review_status: { invited: false },
    },
  };
}
